'use client'

import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { ChevronDown, Search } from 'lucide-react'
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Command, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Input } from '@/components/ui/input'
import { IconComponent } from '@/components/ui/icon'
import { useOptionalStateContext } from '@/components/state-context/state.context'
import { cn } from '@/lib/utils'
import type { Icon } from '@/payload-types'

export type StatesCardDefaultOpen = 'none' | 'first' | 'all'
export type StatesCardDisplayMode = 'accordion' | 'single'

export type PreparedStateEntry = {
  id: string
  name: string
  slug: string
  abbreviation: string
  /** Lower-cased name + abbreviation, precomputed on the server. */
  searchText: string
  /** Pre-rendered template JSX produced on the server, with per-state values resolved. */
  rendered: ReactNode
}

type StatesCardClientProps = {
  title?: string
  displayMode?: StatesCardDisplayMode
  searchPlaceholder?: string
  defaultOpen?: StatesCardDefaultOpen
  selectorLabel?: string
  emptyStateText?: string
  icon?: string | Icon | null
  entries: PreparedStateEntry[]
}

export function StatesCardClient({
  title,
  displayMode = 'accordion',
  searchPlaceholder,
  defaultOpen = 'none',
  selectorLabel,
  emptyStateText,
  icon,
  entries,
}: StatesCardClientProps) {
  if (!entries.length) return null

  if (displayMode === 'single') {
    return (
      <StatesCardSingle
        title={title}
        selectorLabel={selectorLabel}
        emptyStateText={emptyStateText}
        icon={icon}
        entries={entries}
      />
    )
  }

  return (
    <StatesCardAccordion
      title={title}
      searchPlaceholder={searchPlaceholder}
      defaultOpen={defaultOpen}
      entries={entries}
    />
  )
}

function StatesCardAccordion({
  title,
  searchPlaceholder,
  defaultOpen = 'none',
  entries,
}: {
  title?: string
  searchPlaceholder?: string
  defaultOpen?: StatesCardDefaultOpen
  entries: PreparedStateEntry[]
}) {
  const [query, setQuery] = useState('')

  const normalizedQuery = query.trim().toLowerCase()
  const hasQuery = normalizedQuery.length > 0

  const filteredEntries = useMemo(() => {
    if (!hasQuery) return entries
    return entries.filter((entry) => entry.searchText.includes(normalizedQuery))
  }, [entries, hasQuery, normalizedQuery])

  return (
    <section className="flex w-full flex-col gap-4">
      {title ? <h3 className="text-lg font-bold">{title}</h3> : null}

      <div className="relative w-full">
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={searchPlaceholder ?? 'Search states\u2026'}
          aria-label={searchPlaceholder ?? 'Search states'}
          className="pl-9"
        />
      </div>

      {filteredEntries.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No states match &ldquo;{query}&rdquo;.
        </p>
      ) : (
        <Card className="w-full">
          <CardContent className="divide-y divide-black p-0">
            {filteredEntries.map((entry, index) => (
              <StatesCardRow
                key={`${entry.id}-${hasQuery ? 'search' : 'default'}`}
                entry={entry}
                defaultOpen={isOpenByDefault(defaultOpen, index, hasQuery)}
              />
            ))}
          </CardContent>
        </Card>
      )}
    </section>
  )
}

function isOpenByDefault(
  defaultOpen: StatesCardDefaultOpen,
  index: number,
  hasQuery: boolean,
): boolean {
  if (hasQuery) return true
  if (defaultOpen === 'all') return true
  if (defaultOpen === 'first') return index === 0
  return false
}

function StatesCardRow({
  entry,
  defaultOpen,
}: {
  entry: PreparedStateEntry
  defaultOpen: boolean
}) {
  return (
    <Collapsible className="group" defaultOpen={defaultOpen}>
      <CollapsibleTrigger asChild>
        <div
          className={cn(
            'flex w-full cursor-pointer flex-row items-center justify-between gap-3 bg-white p-4 py-3 font-bold transition-colors hover:bg-neutral-100',
            'group-data-[state=open]:border-b group-data-[state=open]:border-black group-data-[state=open]:bg-neutral-100',
          )}
        >
          <span className="min-w-0 flex-1 text-left">{entry.name}</span>
          <ChevronDown
            aria-hidden="true"
            className="shrink-0 text-muted-foreground transition-transform duration-200 -rotate-90 group-data-[state=open]:rotate-0"
          />
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="bg-white p-theme-md md:p-theme-lg">{entry.rendered}</div>
      </CollapsibleContent>
    </Collapsible>
  )
}

const STATE_COOKIE_NAME = 'user-state'
const STATE_LOCAL_STORAGE_KEY = 'state'

function getCookieValue(name: string): string | null {
  if (typeof document === 'undefined') return null
  const cookie = document.cookie
    .split('; ')
    .find((entry) => entry.startsWith(`${name}=`))
    ?.split('=')[1]
  return cookie ? decodeURIComponent(cookie) : null
}

function normalizeStateAbbreviation(value?: string | null): string | null {
  const normalized = value?.trim().toUpperCase()
  return normalized && /^[A-Z]{2}$/.test(normalized) ? normalized : null
}

/**
 * Resolves the visitor's state the same way the event carousel does:
 * prefer the shared `StateContext` (which itself falls back to localStorage,
 * then geolocation), then read localStorage directly, then the `user-state`
 * cookie set by `setStateByAbbrev`. This keeps the card auto-filled even if
 * it's ever rendered without a `StateProvider` ancestor.
 */
function getStoredStateAbbreviation(contextState?: string | null): string | null {
  let localStorageState: string | null = null
  try {
    localStorageState = window.localStorage.getItem(STATE_LOCAL_STORAGE_KEY)
  } catch {
    localStorageState = null
  }

  return (
    normalizeStateAbbreviation(contextState) ||
    normalizeStateAbbreviation(localStorageState) ||
    normalizeStateAbbreviation(getCookieValue(STATE_COOKIE_NAME))
  )
}

function groupEntriesByFirstLetter(
  entries: PreparedStateEntry[],
): Record<string, PreparedStateEntry[]> {
  return entries.reduce<Record<string, PreparedStateEntry[]>>((acc, entry) => {
    const letter = entry.name.charAt(0).toUpperCase()
    if (!acc[letter]) acc[letter] = []
    acc[letter].push(entry)
    return acc
  }, {})
}

function StatesCardSingle({
  title,
  selectorLabel = 'Select your state',
  emptyStateText,
  icon,
  entries,
}: {
  title?: string
  selectorLabel?: string
  emptyStateText?: string
  icon?: string | Icon | null
  entries: PreparedStateEntry[]
}) {
  const stateContext = useOptionalStateContext()
  const [open, setOpen] = useState(false)
  const [selectedAbbreviation, setSelectedAbbreviation] = useState<string | null>(
    () => normalizeStateAbbreviation(stateContext?.currentState) ?? null,
  )

  useEffect(() => {
    const resolved = getStoredStateAbbreviation(stateContext?.currentState)
    if (resolved) {
      setSelectedAbbreviation((prev) => (prev === resolved ? prev : resolved))
    }
    // Only re-run when the shared context's state changes; localStorage/cookie
    // are re-read as part of resolving that same update.
  }, [stateContext?.currentState])

  const selectedEntry = useMemo(
    () => entries.find((entry) => entry.abbreviation === selectedAbbreviation) ?? null,
    [entries, selectedAbbreviation],
  )

  const groupedEntries = useMemo(() => groupEntriesByFirstLetter(entries), [entries])

  const handleSelect = (entry: PreparedStateEntry) => {
    setSelectedAbbreviation(entry.abbreviation)
    setOpen(false)
    stateContext?.setState(entry.abbreviation)
  }

  return (
    <section className="flex w-full flex-col gap-4">
      <Card className="w-full text-left">
        <CardHeader className="flex flex-row items-center gap-3">
          {icon ? <IconComponent icon={icon} className="h-10 w-10 shrink-0" /> : null}
          <CardTitle className="min-w-0 flex-1">{title}</CardTitle>
          <CardAction>
            <Popover open={open} onOpenChange={setOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className="flex shrink-0 flex-row items-center gap-1 rounded-full bg-black px-4 py-2 text-sm text-white"
                >
                  {selectedEntry?.name || selectorLabel}
                  <ChevronDown className="h-4 w-4" />
                </button>
              </PopoverTrigger>
              <PopoverContent className="p-0" align="end">
                <Command>
                  <CommandInput placeholder="Search for your state..." />
                  <CommandList>
                    {Object.entries(groupedEntries)
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([letter, entriesInGroup]) => (
                        <CommandGroup key={letter} heading={letter}>
                          {entriesInGroup.map((entry) => (
                            <CommandItem
                              key={entry.id}
                              value={entry.name}
                              onSelect={() => handleSelect(entry)}
                              className="cursor-pointer"
                            >
                              {entry.name}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      ))}
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </CardAction>
        </CardHeader>
        <CardContent className="p-theme-md md:p-theme-lg">
          {selectedEntry ? (
            selectedEntry.rendered
          ) : (
            <p className="theme-card-accent text-sm">{emptyStateText}</p>
          )}
        </CardContent>
      </Card>
    </section>
  )
}
